import _ from 'axios';
import configs from '../../../conf';
import { Auth } from 'aws-amplify';
import axiosRetry from 'axios-retry/index';

const config = configs[process.env.APP_CONF]; // @TODO now it uses development on local, so make sure NODE_ENV is set

const vormatsApiUrl = `${config.vormatsApi}`;

_.interceptors.response.use(
  response => response,
  error => {
    const originalRequest = error.config;
    if (error.response?.status === 500 && !originalRequest._retry) {
      originalRequest._retry = true;
      return _(originalRequest);
    }
    return Promise.reject(error);
  }
);

axiosRetry(_, {
  retries: 3,
  retryDelay: retryCount => retryCount * 1000,
});

// Get access token for CMS API
// TODO: Check if token is valid, only if not valid, get a new one

let accessToken = {};
let requestConfig = {};

async function token() {
  const response = await _.get('/token');
  if (response) {
    accessToken = { ...response.data, updatedAt: Date.now() };
    requestConfig = {
      headers: {
        Authorization: 'bearer ' + response.data.access_token,
      },
    };
  }
}

token();

/**
 * Implementation of CmsApi.kt in mobile app project
 */
class VormatsAPI {
  static async checkAccessToken() {
    if (Date.now() - accessToken.updatedAt > accessToken.expires_in * 1000) {
      await token();
    }
  }

  static async validIdToken() {
    let token = await this.getAccessJwtToken();

    return {
      headers: {
        Authorization: 'bearer ' + token,
      },
    };
  }

  static async getAccessJwtToken() {
    // Auth.currentSession() checks if token is expired and refreshes with Cognito if needed automatically

    // Check if user has logged in with SSO
    if (localStorage.getItem('aws-amplify-federatedInfo') !== null) {
      const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
      console.log('currentAuthenticatedUser', currentAuthenticatedUser);
      return currentAuthenticatedUser.token;
    } else {
      const session = await Auth.currentSession();
      console.log('session', session);
      return session.idToken.jwtToken;
    }
  }

  static async getAssignments() {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/assignments/stories`, headerConfig).then(response => response.data);
  }

  static async inviteUser(storyId, sceneIds, userEmail, message, allowReadFullStory, allowUpdateFullStory, expiresAt) {
    const headerConfig = await this.validIdToken();
    const storyActions = {
      read: allowReadFullStory,
      update: allowUpdateFullStory,
    };
    return _.post(
      `${vormatsApiUrl}/assignments/stories/${storyId}`,
      { sceneIds, userEmail, message, storyActions, expiresAt },
      headerConfig
    ).then(response => response.data);
  }

  static async sendReminder(storyId, username) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/assignments/stories/${storyId}/remind/${username}`, {}, headerConfig).then(
      response => response.data
    );
  }

  static async getVideoAssignmentsList(storyId, signal) {
    const headerConfig = await this.validIdToken();
    if (signal) {
      headerConfig.signal = signal;
    }
    return _.get(`${vormatsApiUrl}/assignments/stories/${storyId}`, headerConfig).then(response => response.data);
  }

  static async getStoryPermissions(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/permissions/stories/${storyId}`, headerConfig).then(response => response.data);
  }

  static async getStoryPermissionsMe(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/permissions/stories/${storyId}/me`, headerConfig).then(response => response.data);
  }

  static async bulkUpdateAssignments(storyId, users) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/assignments/stories/${storyId}`, { users }, headerConfig).then(response => response.data);
  }

  static async updateUserPermissions(storyId, users) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/permissions/stories/${storyId}`, { users }, headerConfig).then(response => response.data);
  }

  static async getIntroOutroConfig(orientation, language) {
    const headerConfig = await this.validIdToken();

    return _.get(`${vormatsApiUrl}/config/intro-outros?orientation=${orientation}&language=${language}`, headerConfig).then(
      response => response.data
    );
  }

  static async readAssignments(storyId) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/assignments/stories/${storyId}/read`, {}, headerConfig).then(response => response.data);
  }

  /**
   * @return {Promise<Array>}
   */
  static async getDefaultSubtitlesSettings() {
    const headerConfig = await this.validIdToken();
    const response = await _.get(`${vormatsApiUrl}/subtitles/settings`, headerConfig);
    return response.data;
  }

  static async getSubtitlesSettings(companyId) {
    const headerConfig = await this.validIdToken();
    const response = await _.get(`${vormatsApiUrl}/companies/${companyId}/subtitles-settings`, headerConfig);
    return response.data;
  }

  static async updateSubtitlesSettings(companyId, settings) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/companies/${companyId}/subtitles-settings`, settings, headerConfig).then(response => response.data);
  }

  static async completeAssignments(storyId, payload = {}) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/assignments/stories/${storyId}/completion`, payload, headerConfig).then(response => response.data);
  }

  static async updateUserAssignmentsCompletion(storyId, userId, payload = {}) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/assignments/stories/${storyId}/users/${userId}/completion`, payload, headerConfig).then(
      response => response.data
    );
  }

  static async completeAssignment(storyId, payload = {}) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/assignments/stories/${storyId}/mine/completion`, payload, headerConfig).then(
      response => response.data
    );
  }

  static async getStoryChapters(storyId) {
    return _.get(`${vormatsApiUrl}/chapters/stories/${storyId}`).then(response => response.data);
  }

  static async updateStoryChapters(storyId, chapters) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/chapters/stories/${storyId}`, { chapters }, headerConfig).then(response => response.data);
  }

  static async getStoryCallActions(storyId) {
    return _.get(`${vormatsApiUrl}/stories/${storyId}/cta`).then(response => response.data);
  }

  static async getStoryCallAction(actionId) {
    return _.get(`${vormatsApiUrl}/cta/${actionId}`).then(response => response.data);
  }

  static async createStoryCallAction(action) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/cta`, action, headerConfig).then(response => response.data);
  }

  static async updateStoryCallAction(actionId, action) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/cta/${actionId}`, action, headerConfig).then(response => response.data);
  }

  static async removeStoryCallAction(actionId) {
    const headerConfig = await this.validIdToken();
    return _.delete(`${vormatsApiUrl}/cta/${actionId}`, headerConfig).then(response => response.data);
  }

  static async getCallActionUploadUrl(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/cta/${storyId}/upload-url`, headerConfig).then(response => response.data);
  }

  static async getFontsList(companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    const response = await _.get(`${vormatsApiUrl}/fonts`, headerConfig);
    return response.data;
  }

  static async getStoryboardTemplates(params = {}, companyId = null) {
    const headerConfig = await this.validIdToken();

    headerConfig.params = {};

    const { language, isCustom } = params;

    if (language) {
      headerConfig.params.language = language;
    }

    if (isCustom) {
      headerConfig.params.showCustomOnly = true;
    }

    if (companyId) {
      headerConfig.params.companyId = companyId;
    }

    return _.get(`${vormatsApiUrl}/storyboard-generator/templates`, headerConfig).then(response => response.data);
  }

  static async getStoryboardCategories() {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/storyboard-generator/categories`, headerConfig).then(response => response.data);
  }

  static async createStoryboard(payload) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/storyboard-generator/generate`, payload, headerConfig).then(response => response.data);
  }

  static async getStoryStoryboardRequest(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/storyboard-generator/requests/${storyId}`, headerConfig).then(response => response.data);
  }

  static async getDefaultStoryboardTemplates(language) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/storyboard-generator/templates/default?language=${language}`, headerConfig).then(
      response => response.data
    );
  }

  static async createStoryboardTemplate(payload, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.post(`${vormatsApiUrl}/storyboard-generator/templates`, payload, headerConfig).then(response => response.data);
  }

  static async updateStoryboardTemplate(templateId, payload, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/storyboard-generator/templates/${templateId}`, payload, headerConfig).then(
      response => response.data
    );
  }

  static async removeStoryboardTemplate(templateId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.delete(`${vormatsApiUrl}/storyboard-generator/templates/${templateId}`, headerConfig).then(response => response.data);
  }

  static async getCompanyInfo(companyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/companies/${companyId}`, headerConfig).then(response => response.data);
  }

  static async updateCompanyInfo(companyId, payload) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/companies/${companyId}`, payload, headerConfig).then(response => response.data);
  }

  static async getStoryAnalytics(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/analytics/stories/${storyId}`, headerConfig).then(response => response.data);
  }

  static async getCompanyUsers(params = {}, companyId = null) {
    const headerConfig = await this.validIdToken();

    headerConfig.params = params;

    if (companyId) {
      headerConfig.params.companyId = companyId;
    }

    return _.get(`${vormatsApiUrl}/users`, headerConfig).then(response => response.data);
  }

  static async getUserListAsCsv(companyId) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.get(`${vormatsApiUrl}/users/export`, headerConfig).then(response => response.data);
  }

  static async importUsersFromCsv(payload = {}, params = {}, companyId = null) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = params;

    if (companyId) {
      headerConfig.params.companyId = companyId;
    }

    return _.post(`${vormatsApiUrl}/users/import`, payload, headerConfig).then(response => response.data);
  }

  static async getCompanyUser(userId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.get(`${vormatsApiUrl}/users/${userId}`, headerConfig).then(response => response.data);
  }

  static async createCompanyUser(payload, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.post(`${vormatsApiUrl}/users`, payload, headerConfig).then(response => response.data);
  }

  static async editCompanyUser(userId, payload, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/users/${userId}`, payload, headerConfig).then(response => response.data);
  }

  static async activateCompanyUser(userId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/users/${userId}/activate`, {}, headerConfig).then(response => response.data);
  }

  static async deactivateCompanyUser(userId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/users/${userId}/deactivate`, {}, headerConfig).then(response => response.data);
  }

  static async deleteCompanyUser(userId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.delete(`${vormatsApiUrl}/users/${userId}`, headerConfig).then(response => response.data);
  }

  static async resetPasswordForCompanyUser(userId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/users/${userId}/reset-password`, {}, headerConfig).then(response => response.data);
  }

  static async getLogos(companyId) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = { perPage: 50 };
    if (companyId) {
      headerConfig.params.companyId = companyId;
    }
    return _.get(`${vormatsApiUrl}/logos`, headerConfig).then(response => response.data);
  }

  static async getLogo(logoId, companyId) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.get(`${vormatsApiUrl}/logos/${logoId}`, headerConfig).then(response => response.data);
  }

  /**
   * @param {File} file
   * @param [fileName]
   * @param [companyId]
   * @returns {Promise<string>}
   */
  static async uploadLogo(file, fileName, companyId) {
    const headerConfig = await this.validIdToken();
    const extensionDotIndex = file.name.lastIndexOf('.');
    if (extensionDotIndex === -1) {
      throw new Error('Unable to determine file type for logo.');
    }
    let fileExtension = file.name.substring(extensionDotIndex + 1);
    if (!fileExtension) {
      throw new Error('Unable to determine file type for logo.');
    }
    if (fileExtension === 'jpg') {
      fileExtension = 'jpeg';
    }
    headerConfig.params = { fileName, fileExtension };
    if (companyId) {
      headerConfig.params.companyId = companyId;
    }
    const { url, fields } = await _.get(`${vormatsApiUrl}/logos/upload-url`, headerConfig).then(response => response.data);
    return this.uploadPostForm(file, url, fields);
  }

  /**
   * Uploads a file via a POST request with form data.
   * @param {Blob} file
   * @param {string} url
   * @param {Object} fields
   * @returns {Promise<string>} the resource the logo is located at.
   */
  static async uploadPostForm(file, url, fields) {
    const form = new FormData();
    Object.entries(fields).forEach(([field, value]) => {
      form.append(field, value);
    });
    form.append('Content-Type', file.type);
    form.append('file', file);
    await _.post(url, form).then(response => response.data);
    return `${url}${fields.key}`;
  }

  /**
   * Uploads a file via a POST request with form data.
   * @param {Blob} file
   * @param {string} url
   * @param {Object} fields
   * @returns {Promise<string>} the resource the logo is located at.
   */
  static async uploadPostForm(file, url, fields) {
    const form = new FormData();
    Object.entries(fields).forEach(([field, value]) => {
      form.append(field, value);
    });
    form.append('Content-Type', file.type);
    form.append('file', file);
    await _.post(url, form).then(response => response.data);
    return `${url}${fields.key}`;
  }

  static async createLogo(payload, companyId) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.post(`${vormatsApiUrl}/logos`, payload, headerConfig).then(response => response.data);
  }

  static async updateLogo(logoId, logoName, payload, companyId) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = {};

    if (logoName) {
      headerConfig.params = { fileName: logoName };
    }
    if (companyId) {
      headerConfig.params.companyId = companyId;
    }
    return _.put(`${vormatsApiUrl}/logos/${logoId}`, payload, headerConfig).then(response => response.data);
  }

  static async updateLogos(payload, companyId) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/logos`, payload, headerConfig).then(response => response.data);
  }

  static async deleteLogo(logoId, companyId) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.delete(`${vormatsApiUrl}/logos/${logoId}`, headerConfig).then(response => response.data);
  }

  static async resendUserInvite(userId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/users/${userId}/resend-confirmation`, {}, headerConfig).then(response => response.data);
  }

  static async getUserWithRole() {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/users/me`, headerConfig).then(response => response.data);
  }

  static async getCompaniesList() {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/companies`, headerConfig).then(response => response.data);
  }

  static async getFontsListForManagement(params) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = params;
    return _.get(`${vormatsApiUrl}/fonts/all`, headerConfig).then(response => response.data);
  }

  static async getFontById(id) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/fonts/${id}`, headerConfig).then(response => response.data);
  }

  static async uploadFont(file) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = { fileName: file.name };
    const { url, fields } = await _.get(`${vormatsApiUrl}/fonts/upload-url`, headerConfig).then(response => response.data);
    return this.uploadPostForm(file, url, fields);
  }

  static async getFontUploadUrl(fileName) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = { fileName };
    return _.get(`${vormatsApiUrl}/fonts/upload-url`, headerConfig).then(response => response.data);
  }

  static async createFont(payload) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/fonts`, payload, headerConfig).then(response => response.data);
  }

  static async updateFont(id, payload) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/fonts/${id}`, payload, headerConfig).then(response => response.data);
  }

  static async deleteFont(id) {
    const headerConfig = await this.validIdToken();
    return _.delete(`${vormatsApiUrl}/fonts/${id}`, headerConfig).then(response => response.data);
  }

  static async getTextPanels(companyId = null) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = { perPage: 50 };
    if (companyId) {
      headerConfig.params.companyId = companyId;
    }

    return _.get(`${vormatsApiUrl}/text-panels`, headerConfig).then(response => response.data);
  }

  static async getTextPanel(textPanelId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.get(`${vormatsApiUrl}/text-panels/${textPanelId}`, headerConfig).then(response => response.data);
  }

  static async createTextPanel(payload, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.post(`${vormatsApiUrl}/text-panels`, payload, headerConfig).then(response => response.data);
  }

  static async updateTextPanel(textPanelId, payload, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.put(`${vormatsApiUrl}/text-panels/${textPanelId}`, payload, headerConfig).then(response => response.data);
  }

  static async deleteTextPanel(textPanelId, companyId) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.delete(`${vormatsApiUrl}/text-panels/${textPanelId}`, headerConfig).then(response => response.data);
  }

  /**
   * @typedef UsedMinutes
   * @property {number} total
   * @property {number} deleted
   * @property {number} remaining
   */

  /**
   * @typedef CompanyUsage
   * @property {string} id
   * @property {number} companyId
   * @property {string} startDate
   * @property {string} endDate
   * @property {number} purchasedMinutes
   * @property {UsedMinutes} usedMinutes
   */

  /**
   * @param {number} companyId
   * @return {Promise<CompanyUsage[]>}
   */
  static async getCompanyUsages(companyId) {
    const tokenConfig = await this.validIdToken();
    const response = await _.get(`${vormatsApiUrl}/usage/${companyId}`, tokenConfig);
    return response.data;
  }

  /**
   * @typedef UsagePayload
   * @property {string=} startDate
   * @property {string=} endDate
   * @property {number=} purchasedMinutes
   */

  /**
   * @param {string} usageId
   * @param {UsagePayload} payload
   */
  static async updateCompanyUsage(usageId, payload) {
    const tokenConfig = await this.validIdToken();
    await _.put(`${vormatsApiUrl}/usage/${usageId}`, payload, tokenConfig);
  }

  /**
   * @param {number} companyId
   * @param {UsagePayload} payload
   */
  static async createCompanyUsage(companyId, payload) {
    const tokenConfig = await this.validIdToken();
    await _.post(`${vormatsApiUrl}/usage/${companyId}`, payload, tokenConfig);
  }

  static async sendNewEmail(email, storyId) {
    return _.post(`${vormatsApiUrl}/book-demo`, { email, storyId }).then(response => response.data);
  }

  /**
   * @param id
   * @return {Promise<{ id }>}
   */
  static async createFromTemporaryStory(id) {
    const tokenConfig = await this.validIdToken();
    const response = await _.post(`${vormatsApiUrl}/assistant/stories/associate`, { id }, tokenConfig);
    return response.data;
  }

  static async getUsagePerUser(companyId, contractId, params) {
    const tokenConfig = await this.validIdToken();
    tokenConfig.params = params;
    const response = await _.get(`${vormatsApiUrl}/usage/${companyId}/${contractId}`, tokenConfig);
    return response.data;
  }

  static async getStoryGeneralVoiceovers(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/audio/${storyId}/tracks`, headerConfig).then(response => response.data);
  }

  static async createStoryGeneralVoiceovers(storyId) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/audio/${storyId}/tracks`, {}, headerConfig).then(response => response.data);
  }

  static async getVoiceoverUploadUrl(storyId, trackId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/audio/${storyId}/tracks/${trackId}/segments/upload-url`, headerConfig).then(
      response => response.data
    );
  }

  static async getStoryVoiceovers(storyId, trackId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/audio/${storyId}/tracks/${trackId}/segments`, headerConfig).then(response => response.data);
  }

  static async updateStoryVoiceovers(storyId, trackId, payload) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/audio/${storyId}/tracks/${trackId}/segments`, payload, headerConfig).then(
      response => response.data
    );
  }

  static async applyStoryVoiceovers(storyId) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/stories/${storyId}/apply-audio`, {}, headerConfig).then(response => response.data);
  }

  static async editStoryboard(storyId, prompt = '', language = null, signal = null) {
    const headerConfig = await this.validIdToken();
    if (signal) {
      headerConfig.signal = signal;
    }
    const payload = { prompt, language };
    return await _.put(`${vormatsApiUrl}/storyboard-generator/${storyId}/edit`, payload, headerConfig).then(response => response.data);
  }

  static async getCompanyTags(companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }

    return _.get(`${vormatsApiUrl}/tags`, headerConfig).then(response => response.data);
  }

  static async addCompanyTag(name, color, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }

    return _.post(`${vormatsApiUrl}/tags`, { name, color }, headerConfig).then(response => response.data);
  }

  static async removeCompanyTag(tagId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }

    return _.delete(`${vormatsApiUrl}/tags/${tagId}`, headerConfig).then(response => response.data);
  }

  static async assignTagToStory(tagId, storyId) {
    const headerConfig = await this.validIdToken();

    return _.put(`${vormatsApiUrl}/tags/${tagId}/stories/${storyId}`, {}, headerConfig).then(response => response.data);
  }

  static async removeTagFromStory(tagId, storyId) {
    const headerConfig = await this.validIdToken();

    return _.delete(`${vormatsApiUrl}/tags/${tagId}/stories/${storyId}/remove`, headerConfig).then(response => response.data);
  }

  static async downloadStoryMetadata(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/stories/${storyId}/download-resources`, headerConfig).then(response => response.data);
  }

  static async markdownToPdf(markdown) {
    const response = await fetch('/md-to-pdf', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ markdown: markdown })
    });

    return response.blob();
  }

  static async getStoryVoiceoverTrack(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/audio/${storyId}/tracks/current`, headerConfig).then(response => response.data);
  }

  static async enhanceSceneAudio(storyId, sceneId) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/audio/${storyId}/scenes/${sceneId}/enhance`, {}, headerConfig).then(response => response.data);
  }

  static async removeEnhanceScene(storyId, sceneId) {
    const headerConfig = await this.validIdToken();
    return _.delete(`${vormatsApiUrl}/audio/${storyId}/scenes/${sceneId}/enhance`, headerConfig).then(response => response.data);
  }

  /**
 * @param {File} file
 * @param [fileName]
 * @param [companyId]
 * @returns {Promise<string>}
 */
  static async uploadExternalEditedVideo(storyId, file) {
    const headerConfig = await this.validIdToken();
    const extensionDotIndex = file.name.lastIndexOf('.');
    if (extensionDotIndex === -1) {
      throw new Error('Unable to determine file type for video.');
    }
    let fileExtension = file.name.substring(extensionDotIndex + 1);
    if (!fileExtension) {
      throw new Error('Unable to determine file type for video.');
    }
    headerConfig.params = { fileExtension };
    const { url, fields } = await _.get(`${vormatsApiUrl}/stories/${storyId}/external/upload-url`, headerConfig).then(response => response.data);
    return this.uploadPostForm(file, url, fields);
  }

  /**
   * 
   * @param {*} storyId 
   * @param {*} name 
   * @param {*} url 
   * @returns 
   */
  static async createExternalVideoVersion(storyId, name, url) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/stories/${storyId}/external`, { name, url }, headerConfig).then(response => response.data);
  }

  /**
   * 
   * @param {*} storyId 
   * @param {*} resourceId 
   * @returns 
   */
  static async deleteExternalVideoVersion(storyId, resourceId) {
    const headerConfig = await this.validIdToken();
    return _.delete(`${vormatsApiUrl}/stories/${storyId}/external/${resourceId}`, headerConfig).then(response => response.data);

  }

  /**
   * Get signed url for scene normalized resource
   * @param {*} storyId 
   * @param {*} sceneId
   */
  static async getDownloadSceneUrl(storyId, sceneId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/stories/${storyId}/download-resources/scenes/${sceneId}`, headerConfig).then(response => response.data);
  }

  static async getAvatarsTemplates() {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/avatars`, headerConfig).then(response => response.data);
  }

  static async generateAvatarClip(storyId, sceneId, avatarId, voiceId) {
    const headerConfig = await this.validIdToken();
    const params = { avatarId, voiceId };
    return _.post(`${vormatsApiUrl}/avatars/generate/${storyId}/${sceneId}`, params, headerConfig).then(response => response.data);
  }

  static async getStoryConfig(storyId) {
    return _.get(`${vormatsApiUrl}/stories/${storyId}/config`).then(response => response.data);
  }

  static async generateBatchAvatarClips(storyId, payload) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/avatars/generate/${storyId}`, payload, headerConfig).then(response => response.data);
  }

  static async enhanceSubtitles(storyId) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/subtitles/${storyId}/enhance`, {}, headerConfig).then(response => response.data);
  }

  static async generateVoiceover(storyId, voiceId) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/audio/${storyId}/generate-voiceover`, { voiceId }, headerConfig).then(response => response.data);
  }

  static async getStorySchedule(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/stories/${storyId}/schedule`, headerConfig).then(response => response.data);
  }

  static async createStorySchedule(storyId, payload) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/stories/${storyId}/schedule`, { ...payload, privacy: 'private' }, headerConfig).then(response => response.data);
  }

  static async updateStorySchedule(storyId, payload) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/stories/${storyId}/schedule`, payload, headerConfig).then(response => response.data);
  }

  static async removeSchedule(storyId) {
    const headerConfig = await this.validIdToken();
    return _.delete(`${vormatsApiUrl}/stories/${storyId}/schedule`, headerConfig).then(response => response.data);
  }

  static async updateScheduleNotifications(storyId, notifications) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/stories/${storyId}/schedule/notifications`, { notifications }, headerConfig).then(response => response.data);
  }

  static async getMySchedule() {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/users/schedule`, headerConfig).then(response => response.data);
  }

  static async getStoriesList(query) {
    let config = await this.validIdToken();
    config.params = {
      ...query,
    }
    return _.get(`${vormatsApiUrl}/stories`, config).then(response => response.data);
  }

  static async getUserCalendarIcs() {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/users/calendar`, headerConfig).then(response => response.data);
  }

  /**
   * Features Management
   */
  static async getAllFeatures(params = {}) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = params;
    const response = await _.get(`${vormatsApiUrl}/features`, headerConfig);
    return {
      data: response.data?.data || response.data,
      meta: { total: response.data?.meta?.total || response.data?.total || response.data?.length || 0 },
    };
  }

  static async getCompanyFeatures(companyId, params = {}) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = params;
    const response = await _.get(`${vormatsApiUrl}/features/company/${companyId}`, headerConfig);
    return {
      data: response.data?.data || response.data,
      meta: { total: response.data?.meta?.total || response.data?.total || response.data?.length || 0 },
    };
  }

  static async updateCompanyFeature(companyId, payload) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/features/company/${companyId}`, payload, headerConfig).then(response => response.data);
  }

  static async createFeature(payload) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/features`, payload, headerConfig).then(response => response.data);
  }

  static async updateFeature(id, payload) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/features/${id}`, payload, headerConfig).then(response => response.data);
  }

  static async deleteFeature(id) {
    const headerConfig = await this.validIdToken();
    return _.delete(`${vormatsApiUrl}/features/${id}`, headerConfig).then(response => response.data);
  }

  /**
   * Update (enable/disable/set value) a feature for a user.
   * @param {string} userId - The user ID
   * @param {object} updateUserFeatureDto - The update payload (featureId, enabled, value, userId)
   * @returns {Promise<UserFeatureEntity>}
   */
  static async updateUserFeature(userId, updateUserFeatureDto) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/user-features/${userId}`, updateUserFeatureDto, headerConfig).then(response => response.data);
  }

  /**
   * List all features for a user with pagination and search, using custom user values if present, otherwise company, otherwise default.
   * @param {string} userId - The user ID
   * @param {number} companyId - The company ID
   * @param {object} pageOptions - Pagination options
   * @param {object} listOptions - List/search options
   * @returns {Promise<PageWrapper<UserFeatureEntity>>}
   */
  static async listUserFeatures(userId, companyId, pageOptions = {}, listOptions = {}, userOverridableOnly = false) {
    const headerConfig = await this.validIdToken();
    headerConfig.params = { ...pageOptions, ...listOptions, companyId, userOverridableOnly };
    return _.get(`${vormatsApiUrl}/user-features/${userId}`, headerConfig).then(response => response.data);
  }

  static async processExternalContext(files = [], urls = [], signal = null) {
    const headerConfig = await this.validIdToken();
    if (signal) {
      headerConfig.signal = signal;
    }
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append('files', file);
    });

    if (urls.length > 0) {
      formData.append('urls', JSON.stringify(urls));
    }

    return _.post(`${vormatsApiUrl}/external-context/process`, formData, {
      ...headerConfig,
      headers: {
        ...headerConfig.headers
      },
    }).then(response => response.data);
  }

  static async generateStoryboard(content, language = 'en', contextFileIds = [], orientation = 'landscape', numberOfClips, participants = 'me', contentType = 'summary', version = null, duration = 'short', signal = null) {
    const headerConfig = await this.validIdToken();
    if (signal) {
      headerConfig.signal = signal;
    }
    const payload = {
      content,
      language,
      contextFileIds,
      orientation,
      numberOfClips,
      participants,
      contentType,
      duration,
    };

    let versionString = ""
    if (version) {
      versionString = `/v${version}`;
    }

    return _.post(`${vormatsApiUrl}/assistant/generate-storyboard${versionString}`, payload, headerConfig).then(response => response.data);
  }

  static async generateVideoForStory(storyId) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/stories/${storyId}/generate-video`, {}, headerConfig).then(response => response.data);
  }

  static async getCompanyColors(companyId = null) {
    const config = await this.validIdToken();
    if (companyId) {
      config.params = { companyId };
    }
    return _.get(`${vormatsApiUrl}/colors`, config).then(response => response.data);
  }

  static async addCompanyColor(color, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }

    return _.post(`${vormatsApiUrl}/colors`, { color }, headerConfig).then(response => response.data);
  }

  static async removeCompanyColor(colorId, companyId = null) {
    const headerConfig = await this.validIdToken();
    if (companyId) {
      headerConfig.params = { companyId };
    }
    return _.delete(`${vormatsApiUrl}/colors/${colorId}`, headerConfig).then(response => response.data);
  }

  /**
   * Get templates list for user
   * @param {Object} query - Query parameters
   * @param {string} [query.search] - Search term for template names
   * @param {number} [query.page] - Page number for pagination
   * @param {number} [query.perPage] - Number of items per page
   * @param {boolean} [query.onlyDefault] - Filter to show only default templates
   * @param {boolean} [query.onlyCompany] - Filter to show only company templates
   * @param {number} [query.companyId] - Company ID to filter templates
   * @returns {Promise<Object>} Paginated list of templates
   */
  static async getTemplates(
    query
  ) {
    const headerConfig = await this.validIdToken();

    headerConfig.params = { ...query };
    return _.get(`${vormatsApiUrl}/templates`, headerConfig)
      .then(({ data }) => data);
  }

  /**
   * Create a template from an existing editor state or create a new empty editor state
   * @param {Object} template - The template data
   * @param {string} template.name - Name of the template (max 50 characters)
   * @param {string} template.description - Description of the template (max 255 characters)
   * @param {string} [template.editorStateId] - Editor state id to create template from. If not provided, an empty editor state will be created.
   * @param {string} [template.aspectRatio] - Aspect ratio for the empty editor state. Required when editorStateId is not provided. Must be one of: '16:9', '9:16', '1:1', '4:5'
   * @param {number} [template.companyId] - Company ID that owns the template
   * @returns {Promise<Object>} The created template
   */
  static async createTemplate(template) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/templates`, {
      ...template
    }, headerConfig)
      .then(({ data }) => data);
  }

  static async getTemplateById(id) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/templates/${id}`, headerConfig)
      .then(({ data }) => data);
  }

  /**
   * Update template by ID
   * @param {string} id - The template ID
   * @param {Object} template - The template update data
   * @param {string} [template.name] - Name of the template (max 30 characters)
   * @param {string} [template.description] - Description of the template (max 255 characters)
   * @param {string} [template.thumbnail] - Thumbnail URL of the template
   * @param {number} [template.companyId] - Company ID that owns the template
   * @returns {Promise<Object>} The updated template
   */
  static async updateTemplate(
    id,
    template,
  ) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/templates/${id}`, {
      ...template
    }, headerConfig)
      .then(({ data }) => data);
  }

  static async getTemplateThumbnailUploadUrl(
    id,
    fileExtension) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/templates/${id}/upload-url`, { fileExtension }, headerConfig)
      .then(({ data }) => data);
  }

  static async deleteTemplate(id) {
    const headerConfig = await this.validIdToken();
    return _.delete(`${vormatsApiUrl}/templates/${id}`, headerConfig)
      .then(({ data }) => data);
  }

  /**
   * Duplicate a template
   * @param {string} templateId - The template ID to duplicate
   * @returns {Promise<Object>} The duplicated template
   */
  static async duplicateTemplate(templateId) {
    const headerConfig = await this.validIdToken();
    return _.post(`${vormatsApiUrl}/templates/${templateId}/duplicate`, {}, headerConfig)
      .then(({ data }) => data);
  }

  /**
   * Update editor state for a template
   * @param {string} templateId - The template ID
   * @param {Object} editorState - The editor state data
   * @returns {Promise<Object>} The updated editor state
   */
  static async updateTemplateEditorState(templateId, editorState) {
    const headerConfig = await this.validIdToken();
    return _.put(`${vormatsApiUrl}/templates/${templateId}/editor-state`, editorState, headerConfig)
      .then(({ data }) => data);
  }

  /**
   * Get the editor preference for a story
   * @param {string} storyId - The story ID
   * @returns {Promise<Object|null>} The editor preference or null if not found
   */
  static async getStoryEditorPreference(storyId) {
    const headerConfig = await this.validIdToken();
    return _.get(`${vormatsApiUrl}/stories/${storyId}/editor-preference`, headerConfig)
      .then(response => response.data);
  }

  /**
   * Update the editor preference for a story
   * @param {string} storyId - The story ID
   * @param {string} editorType - The editor type ('basic' or 'advanced')
   * @param {boolean} doNotAskAgain - Whether to not ask the user again about editor preference
   * @returns {Promise<Object>} The updated editor preference
   */
  static async updateStoryEditorPreference(storyId, editorType, doNotAskAgain = false) {
    const headerConfig = await this.validIdToken();
    const payload = {
      editorType,
      doNotAskAgain,
    };
    return _.put(`${vormatsApiUrl}/stories/${storyId}/editor-preference`, payload, headerConfig)
      .then(response => response.data);
  }
}

export default VormatsAPI;
